import {FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {Role} from '../types/domain'
import {useAuth} from '../modules/auth'
import {RenterLayout} from '../pages/renter/RenterLayout'
import {RenterHome} from '../pages/renter/RenterHome'
import {ProfilePage} from '../pages/renter/ProfilePage'
import {ApplyPage} from '../pages/renter/ApplyPage'
import {BookPage} from '../pages/renter/BookPage'
import {RenterPaymentsPage} from '../pages/renter/RenterPaymentsPage'
import {AdminDashboardWrapper} from '../pages/dashboard/AdminDashboard'
import {AccountsPage} from '../pages/accounts/AccountsPage'
import {ApplicationsPage} from '../pages/applications/ApplicationsPage'
import {BedsPage} from '../pages/beds/BedsPage'
import {BookingsPage} from '../pages/bookings/BookingsPage'
import {PaymentsPage} from '../pages/payments/PaymentsPage'
import {PaymentSettingsPage} from '../pages/payments/PaymentSettingsPage'
import {ListingsPage} from '../pages/listings/ListingsPage'
import {RequestsPage} from '../pages/requests/RequestsPage'
import {CleanerAdminPage} from '../pages/cleaner/CleanerAdminPage'
import {AnnouncementsPage} from '../pages/announcements/AnnouncementsPage'
import {AuditLogPage} from '../pages/audit/AuditLogPage'
import {DoorCodesPage} from '../pages/door-codes/DoorCodesPage'
import {InfoPagesPage} from '../pages/info/InfoPagesPage'
import {SiteGalleryPage} from '../pages/site/SiteGalleryPage'
import {WelcomeEmailPage} from '../pages/welcome-email/WelcomeEmailPage'
import {FlyerPage} from '../pages/flyer/FlyerPage'
import {BedTypesPage} from '../pages/bed-types/BedTypesPage'
import {IntegrationsPage} from '../pages/integrations/IntegrationsPage'
import {SettingsPage} from '../pages/settings/SettingsPage'

// Renter-facing routes — rendered for RENTER accounts inside RenterLayout.
const RenterRoutes = () => {
  return (
    <Routes>
      <Route element={<RenterLayout />}>
        <Route path='home' element={<RenterHome />} />
        <Route path='apply' element={<ApplyPage />} />
        <Route path='book' element={<BookPage />} />
        <Route path='payments' element={<RenterPaymentsPage />} />
        <Route path='profile' element={<ProfilePage />} />
        <Route path='*' element={<Navigate to='/home' />} />
      </Route>
    </Routes>
  )
}

// Admin-facing routes — Crew Crashpad admin panel inside the Metronic layout.
// Live module: Dashboard. The rest route to a "coming soon" placeholder until
// their feature module is built, so the nav matches the full build plan.
const AdminRoutes = () => {
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        <Route path='dashboard' element={<AdminDashboardWrapper />} />

        {/* Upcoming modules — placeholder until built */}
        <Route path='accounts' element={<AccountsPage />} />
        <Route path='applications' element={<ApplicationsPage />} />
        <Route path='beds' element={<BedsPage />} />
        <Route path='bed-types' element={<BedTypesPage />} />
        <Route path='bookings' element={<BookingsPage />} />
        <Route path='payments' element={<PaymentsPage />} />
        <Route path='payment-settings' element={<PaymentSettingsPage />} />
        <Route path='listings' element={<ListingsPage />} />
        <Route path='requests' element={<RequestsPage />} />
        <Route path='cleaner' element={<CleanerAdminPage />} />
        <Route path='announcements' element={<AnnouncementsPage />} />
        <Route path='audit-log' element={<AuditLogPage />} />
        <Route path='door-codes' element={<DoorCodesPage />} />
        <Route path='info-pages' element={<InfoPagesPage />} />
        <Route path='site-gallery' element={<SiteGalleryPage />} />
        <Route path='welcome-email' element={<WelcomeEmailPage />} />
        <Route path='integrations' element={<IntegrationsPage />} />
        <Route path='flyer' element={<FlyerPage />} />
        <Route path='settings' element={<SettingsPage />} />

        <Route path='*' element={<Navigate to='/dashboard' />} />
      </Route>
    </Routes>
  )
}

// Dispatch to the right experience based on the authenticated role. ADMIN gets
// the Metronic admin panel; everyone else (RENTER) gets the renter app.
const PrivateRoutes = () => {
  const {currentUser} = useAuth()
  return currentUser?.appRole === Role.ADMIN ? <AdminRoutes /> : <RenterRoutes />
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {'0': baseColor},
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes, SuspensedView}
